import { useState } from 'react';
import api from '../../services/api';

interface Analise {
  id: number;
  tipo_analise: string;
  titulo: string;
  conteudo: string;
  nivel_confianca?: number;
  prioridade: string;
  modelo_ia: string;
  tokens_utilizados?: number;
  tempo_processamento_ms?: number;
  visualizado: boolean;
  criado_em: string;
}

interface Props {
  analises: Analise[];
  onRecarregar: () => void;
  pacienteNome: string;
}

export default function PainelAnalisesIA({ analises, onRecarregar, pacienteNome }: Props) {
  const [analiseSelecionada, setAnaliseSelecionada] = useState<Analise | null>(null);
  const [arquivando, setArquivando] = useState(false);

  // ========================================
  // MARCAR COMO VISUALIZADA
  // ========================================
  const handleVisualizarAnalise = async (analise: Analise) => {
    setAnaliseSelecionada(analise);

    if (!analise.visualizado) {
      try {
        await api.put(`/analise-paciente/visualizar/${analise.id}`);
        onRecarregar();
      } catch (error) {
        console.error('[PAINEL-IA] Erro ao marcar como visualizada:', error);
      }
    }
  };

  // ========================================
  // ARQUIVAR ANÁLISE
  // ========================================
  const handleArquivar = async (id: number) => {
    if (!confirm('Deseja realmente arquivar esta análise?')) {
      return;
    }

    try {
      setArquivando(true);
      await api.put(`/analise-paciente/arquivar/${id}`);
      alert('✅ Análise arquivada com sucesso');
      setAnaliseSelecionada(null);
      onRecarregar();
    } catch (error: any) {
      console.error('[PAINEL-IA] Erro ao arquivar:', error);
      alert('Erro ao arquivar análise');
    } finally {
      setArquivando(false);
    }
  };

  // ========================================
  // GET BADGE DE PRIORIDADE
  // ========================================
  const getBadgePrioridade = (prioridade: string) => {
    const badges = {
      'baixa': 'badge bg-secondary',
      'media': 'badge bg-primary',
      'alta': 'badge bg-warning',
      'critica': 'badge bg-danger'
    };
    return badges[prioridade as keyof typeof badges] || 'badge bg-secondary';
  };

  // ========================================
  // GET ÍCONE DE TIPO
  // ========================================
  const getIconeTipo = (tipo: string) => {
    const icones: Record<string, string> = {
      'insight_geral': 'fa-lightbulb',
      'evolucao_sintomas': 'fa-chart-line',
      'padroes_humor': 'fa-smile',
      'efetividade_tratamento': 'fa-check-circle',
      'risco_abandono': 'fa-exclamation-triangle',
      'prognostico': 'fa-crystal-ball',
      'recomendacoes': 'fa-clipboard-list',
      'alerta_clinico': 'fa-bell'
    };
    return icones[tipo] || 'fa-file-alt';
  };

  if (analises.length === 0) {
    return (
      <div className="card">
        <div className="card-header bg-success text-white">
          <h3 className="card-title">
            <i className="fas fa-robot me-2"></i>
            Análises com IA - Sophya
          </h3>
        </div>
        <div className="card-body">
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            Nenhuma análise gerada ainda. Use os botões acima para gerar análises com IA.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header bg-success text-white">
        <h3 className="card-title">
          <i className="fas fa-robot me-2"></i>
          Análises com IA - {pacienteNome} ({analises.length})
        </h3>
        <div className="card-tools">
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={onRecarregar}
          >
            <i className="fas fa-sync-alt me-1"></i>
            Atualizar
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="row">
          {/* LISTA DE ANÁLISES */}
          <div className="col-md-4">
            <div className="list-group">
              {analises.map((analise) => (
                <button
                  key={analise.id}
                  type="button"
                  className={`list-group-item list-group-item-action ${
                    analiseSelecionada?.id === analise.id ? 'active' : ''
                  } ${!analise.visualizado ? 'fw-bold' : ''}`}
                  onClick={() => handleVisualizarAnalise(analise)}
                >
                  <div className="d-flex w-100 justify-content-between">
                    <h6 className="mb-1">
                      <i className={`fas ${getIconeTipo(analise.tipo_analise)} me-2`}></i>
                      {analise.titulo}
                    </h6>
                    {!analise.visualizado && (
                      <span className="badge bg-info">Novo</span>
                    )}
                  </div>
                  <small className="text-muted d-block">
                    {new Date(analise.criado_em).toLocaleString('pt-BR')}
                  </small>
                  <span className={getBadgePrioridade(analise.prioridade)}>
                    {analise.prioridade.toUpperCase()}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* VISUALIZAÇÃO DA ANÁLISE */}
          <div className="col-md-8">
            {analiseSelecionada ? (
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">
                    <i className={`fas ${getIconeTipo(analiseSelecionada.tipo_analise)} me-2`}></i>
                    {analiseSelecionada.titulo}
                  </h4>
                  <div className="card-tools">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleArquivar(analiseSelecionada.id)}
                      disabled={arquivando}
                    >
                      {arquivando ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Arquivando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-archive me-1"></i>
                          Arquivar
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  {/* Metadados */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      {analiseSelecionada.nivel_confianca && (
                        <p className="mb-1">
                          <strong>Confiança:</strong> {analiseSelecionada.nivel_confianca}%
                        </p>
                      )}
                    </div>
                    <div className="col-md-6 text-end">
                      {analiseSelecionada.tempo_processamento_ms && (
                        <p className="mb-1">
                          <strong>Tempo:</strong> {(analiseSelecionada.tempo_processamento_ms / 1000).toFixed(1)}s
                        </p>
                      )}
                    </div>
                  </div>

                  <hr />

                  {/* Conteúdo da Análise (Markdown formatado) */}
                  <div 
                    className="analise-conteudo"
                    style={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.6',
                      fontSize: '15px'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatarMarkdown(analiseSelecionada.conteudo)
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="fas fa-hand-pointer me-2"></i>
                Selecione uma análise na lista ao lado para visualizar
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ========================================
// FUNÇÃO: Formatar Markdown para HTML
// ========================================
function formatarMarkdown(texto: string): string {
  if (!texto) return '';

  let html = texto;

  // Títulos
  html = html.replace(/^## (.+)$/gm, '<h4 class="mt-3 mb-2 text-primary">$1</h4>');
  html = html.replace(/^# (.+)$/gm, '<h3 class="mt-3 mb-2 text-primary">$1</h3>');

  // Negrito
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Itálico
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Listas
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul class="mb-2">$&</ul>');

  // Links
  html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank">$1</a>');

  // Code inline
  html = html.replace(/`(.+?)`/g, '<code class="bg-light px-1">$1</code>');

  // Quebras de linha
  html = html.replace(/\n\n/g, '<br><br>');

  // Disclaimer (destaque)
  html = html.replace(/⚠️ IMPORTANTE:/g, '<span class="badge bg-warning text-dark">⚠️ IMPORTANTE:</span>');

  return html;
}
